/**
 * @fileoverview Shared contacts-module preview component for template builders in system-web and tenant-web.
 * @module template-builder
 *
 * Responsibilities:
 * - Render the Contacts module UI inside template preview surfaces.
 * - Track contact add/edit/delete changes and emit them via the onChanges callback.
 * - Support configurable roles, required contact counts, and attribute flags.
 *
 * Dependencies:
 * - ContactsModuleConfig from shared templateTypes
 * - formatPhoneNumber from @firesquirrel/core-domain
 * - lucide-react icons
 *
 * Architecture:
 * - Presentation-only component; no API calls or Dexie access.
 * - Data contract (ContactEntry, ContactChange, onChanges) is the shared
 *   interface between the template builder preview and the mobile inspection app.
 * - Moved here from tenant-web so both system-web and tenant-web share one implementation.
 *
 * Notes:
 * - This file is split into two zones:
 *   ZONE 1: DATA & LOGIC CONTRACT — do not modify types/handler logic when porting.
 *   ZONE 2: UI LAYER — JSX and styling. Safe to redesign per host app's design system.
 * - The component signature and onChanges contract are fixed across all host apps.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Users, Search, Plus, ChevronRight, Trash2 } from 'lucide-react';
import { formatPhoneNumber } from '@firesquirrel/core-domain';
import type {
  ContactsModuleConfig,
  ContactEntry,
  ContactChange,
  PhoneRow,
  EmailRow,
  AddressProximity,
} from '@firesquirrel/core-domain';
import { ContactSearchTypeahead } from '../ContactSearchTypeahead';
import type { PanelContact, NewContactInput } from '../ContactsPanel';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * @contract ContactsModulePreviewMode
 *
 * Purpose:
 * - Selects which add-contact UX is rendered.
 *
 * Fields:
 * - 'session' (default) — keeps the legacy in-memory Find/New bottom-sheet
 *   flow. Suitable for system-web template canvas previews and any other
 *   surface that has no Dexie-backed pool or persistence wiring.
 * - 'persisted' — replaces Find/New with the typeahead-with-create-fallback
 *   header that matches tenant-web ContactsPanel / CaseTimeline. Hosts must
 *   wire `panelContactPool`, `onCreateNew`, and (optionally)
 *   `onLinkExistingToEntity` to satisfy the local-first rule.
 *
 * Sync:
 * - Mode is a render-time flag; persistence happens in host adapters.
 */
export type ContactsModulePreviewMode = 'session' | 'persisted';

// =============================================================================
// ZONE 1 — DATA & LOGIC CONTRACT
// Do NOT modify the types, interfaces, or handler logic below when porting
// to mobile. These are the shared contracts used by the template system.
// =============================================================================


// ── Internal types ──

type PreferredMethod = 'email' | 'phone' | 'mail';

type SheetState =
  | { mode: 'add' }
  | { mode: 'edit'; contact: ContactEntry }
  | { mode: 'pool' }
  | null;

/** Returns a blank PhoneRow. */
const BLANK_PHONE = (): PhoneRow => ({ id: crypto.randomUUID(), number: '', label: '' });

/** Returns a blank EmailRow. */
const BLANK_EMAIL = (): EmailRow => ({ id: crypto.randomUUID(), address: '', label: '' });

/** Returns a blank ContactEntry for the add-new sheet. */
const BLANK_CONTACT = (firstRole: string): ContactEntry => ({
  id: crypto.randomUUID(),
  roles: firstRole ? [firstRole] : [],
  name: '',
  phones: [BLANK_PHONE()],
  emails: [BLANK_EMAIL()],
  phone: '',
  email: '',
  preferredMethod: 'email',
});

// =============================================================================
// ZONE 2 — UI LAYER
// Everything below is presentation only. Host apps may restyle, replace, or
// adapt the JSX to their design system. Keep handler calls intact.
// =============================================================================

/**
 * @component AvatarCircle
 *
 * Purpose:
 * - Shows initials-based avatar circle for a contact entry.
 *
 * Props:
 * - name: Full display name used to derive initials.
 * - size: Size variant; 'sm' or 'md'.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Presentation-only; no data access.
 *
 * Performance:
 * - No memoization required; renders inline string.
 */
function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (name[0] || '?').toUpperCase();
}

function AvatarCircle({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' }) {
  const dim = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';
  return (
    <div className={`${dim} rounded-full bg-blue-600 flex items-center justify-center shrink-0 text-white font-semibold`}>
      {initials(name)}
    </div>
  );
}

/**
 * @component ContactPoolPicker
 *
 * Purpose:
 * - Presents a searchable list of pre-existing contacts to add to the inspection.
 *
 * Props:
 * - pool: Available contact pool entries.
 * - alreadyAdded: IDs already on the form (filtered from results).
 * - onSelect: Called with the chosen ContactEntry.
 * - onCancel: Dismisses the sheet.
 *
 * Data Source:
 * - local (pool is passed in from parent state)
 *
 * Constraints:
 * - No API calls.
 *
 * Performance:
 * - Filters derived from local state; no debounce needed.
 */
function ContactPoolPicker({
  pool,
  alreadyAdded,
  onSelect,
  onCancel,
}: {
  pool: ContactEntry[];
  alreadyAdded: string[];
  onSelect: (c: ContactEntry) => void;
  onCancel: () => void;
}) {
  const [q, setQ] = useState('');
  const filtered = pool.filter(
    (c) =>
      !alreadyAdded.includes(c.id) &&
      (c.name.toLowerCase().includes(q.toLowerCase()) ||
        c.roles.join(' ').toLowerCase().includes(q.toLowerCase()) ||
        c.email.toLowerCase().includes(q.toLowerCase())),
  );

  return (
    <div className="absolute inset-0 z-20 flex flex-col justify-end" style={{ background: 'rgba(0,0,0,0.45)' }}>
      <div className="flex-1" onClick={onCancel} />
      <div className="bg-white rounded-t-2xl shadow-2xl overflow-hidden" style={{ maxHeight: '75%' }}>
        <div className="flex justify-center pt-2.5 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
        <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-100">
          <button onClick={onCancel} className="text-sm text-blue-600 font-medium">Cancel</button>
          <span className="text-sm font-semibold text-gray-800">Add from Case Contacts</span>
          <div className="w-12" />
        </div>
        <div className="px-4 py-2.5 border-b border-gray-100">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-gray-50 focus-within:ring-2 focus-within:ring-blue-300 focus-within:bg-white transition">
            <Search size={13} className="text-gray-400 shrink-0" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, role, or email…"
              className="flex-1 text-sm bg-transparent outline-hidden text-gray-700 placeholder-gray-400"
            />
          </div>
        </div>
        <div className="overflow-y-auto divide-y divide-gray-100" style={{ maxHeight: 320 }}>
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-400">No contacts found</div>
          ) : (
            filtered.map((c) => (
              <button
                key={c.id}
                onClick={() => onSelect(c)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left"
              >
                <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center shrink-0 text-white text-xs font-semibold">
                  {initials(c.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{c.name}</div>
                  <div className="text-xs text-gray-500 truncate">{c.roles.join(', ')}{c.email ? ` · ${c.email}` : ''}</div>
                </div>
                <ChevronRight size={14} className="text-gray-300 shrink-0" />
              </button>
            ))
          )}
        </div>
        <div className="h-5 bg-white" />
      </div>
    </div>
  );
}

/**
 * @component Toggle
 *
 * Purpose:
 * - Simple on/off toggle switch.
 *
 * Props:
 * - on: Current toggle state.
 * - onToggle: Called on click to flip the state.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Presentation-only; caller owns state.
 *
 * Performance:
 * - No memoization required.
 */
function Toggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <div
      onClick={onToggle}
      className={`w-11 h-6 rounded-full transition-colors shrink-0 relative cursor-pointer ${on ? 'bg-blue-600' : 'bg-gray-200'}`}
    >
      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </div>
  );
}

/**
 * @component ContactSheet
 *
 * Purpose:
 * - Bottom-sheet form for adding or editing a contact entry.
 * - Supports multiple phone rows and multiple email rows per contact.
 * - Full-height scrollable body; large primary "Done" button anchored above
 *   the safe-area inset so it is always reachable on touch screens.
 *
 * Props:
 * - initial: Starting ContactEntry values.
 * - roleOptions: Available role suggestions from module config.
 * - submitLabel: Label shown in the header (e.g. "Add Contact").
 * - onSubmit: Called with the final ContactEntry on confirmation.
 * - onCancel: Dismisses the sheet.
 * - onDelete: Optional; shown in edit mode to remove the contact.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - No API calls. Caller handles persistence.
 *
 * Performance:
 * - Local draft state; no external subscriptions.
 */
function ContactSheet({
  initial,
  roleOptions,
  submitLabel,
  onSubmit,
  onCancel,
  onDelete,
}: {
  initial: ContactEntry;
  roleOptions: string[];
  submitLabel: string;
  onSubmit: (c: ContactEntry) => void;
  onCancel: () => void;
  onDelete?: () => void;
}) {
  const ensureRows = (entry: ContactEntry): ContactEntry => ({
    ...entry,
    phones: entry.phones?.length ? entry.phones : [BLANK_PHONE()],
    emails: entry.emails?.length ? entry.emails : [BLANK_EMAIL()],
  });

  const [draft, setDraft] = useState<ContactEntry>(() => ensureRows({ ...initial }));
  const toggle = (k: 'isResponsibleParty' | 'isBillingContact' | 'receivesLetter') =>
    setDraft((d) => ({ ...d, [k]: !d[k] }));

  /** Syncs draft.phone/email shorthands from the first row before submitting. */
  const handleDone = () => {
    const finalDraft: ContactEntry = {
      ...draft,
      phone: draft.phones[0]?.number ?? '',
      email: draft.emails[0]?.address ?? '',
    };
    onSubmit(finalDraft);
  };

  // ── Phone row helpers ──
  const updatePhone = (id: string, field: keyof PhoneRow, value: string) =>
    setDraft((d) => ({
      ...d,
      phones: d.phones.map((p) =>
        p.id === id ? { ...p, [field]: field === 'number' ? formatPhoneNumber(value) : value } : p,
      ),
    }));
  const addPhone = () => setDraft((d) => ({ ...d, phones: [...d.phones, BLANK_PHONE()] }));
  const removePhone = (id: string) =>
    setDraft((d) => ({ ...d, phones: d.phones.filter((p) => p.id !== id) }));

  // ── Email row helpers ──
  const updateEmail = (id: string, field: keyof EmailRow, value: string) =>
    setDraft((d) => ({
      ...d,
      emails: d.emails.map((e) => (e.id === id ? { ...e, [field]: value } : e)),
    }));
  const addEmail = () => setDraft((d) => ({ ...d, emails: [...d.emails, BLANK_EMAIL()] }));
  const removeEmail = (id: string) =>
    setDraft((d) => ({ ...d, emails: d.emails.filter((e) => e.id !== id) }));

  const canSubmit = draft.name.trim().length > 0 && draft.roles.length > 0;

  return (
    <div className="absolute inset-0 z-20 flex flex-col justify-end" style={{ background: 'rgba(0,0,0,0.45)' }}>
      <div className="flex-1" onClick={onCancel} />
      {/* Sheet panel — fills from the bottom up, max 92% of viewport */}
      <div
        className="bg-white rounded-t-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: '92%' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Drag handle ── */}
        <div className="flex justify-center pt-2.5 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* ── Header row: Cancel · title ── */}
        <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-100 shrink-0">
          <button
            onClick={onCancel}
            className="text-[15px] text-blue-600 font-medium min-w-[60px]"
          >
            Cancel
          </button>
          <span className="text-[15px] font-semibold text-gray-900">{submitLabel}</span>
          {/* Spacer keeps title centred */}
          <div className="min-w-[60px]" />
        </div>

        {/* ── Scrollable form body ── */}
        <div className="overflow-y-auto flex-1 min-h-0">
          <div className="px-4 py-3 divide-y divide-gray-100">

            {/* Role — multi-select toggle pills */}
            <div className="pb-4">
              <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                Role <span className="text-gray-300 font-normal normal-case">(select all that apply)</span>
              </label>
              {roleOptions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {roleOptions.map((r) => {
                    const isSelected = draft.roles.includes(r);
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() =>
                          setDraft((d) => ({
                            ...d,
                            roles: isSelected
                              ? d.roles.filter((x) => x !== r)
                              : [...d.roles, r],
                          }))
                        }
                        className={`text-sm px-4 py-2 rounded-full border font-medium transition-colors ${
                          isSelected
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'border-gray-300 text-gray-600 bg-white hover:border-blue-300'
                        }`}
                      >
                        {r}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Full name */}
            <div className="py-4">
              <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                Full Name *
              </label>
              <input
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="Jane Smith"
                className="w-full px-3 py-3 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-blue-300 focus:bg-white transition"
              />
            </div>

            {/* Phone rows */}
            <div className="py-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  Phone
                </label>
                <button
                  type="button"
                  onClick={addPhone}
                  className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
                >
                  <Plus size={12} /> Add phone
                </button>
              </div>
              <div className="space-y-2">
                {draft.phones.map((p, idx) => (
                  <div key={p.id} className="flex gap-2 items-start">
                    <div className="flex-1 space-y-1">
                      <input
                        type="tel"
                        inputMode="tel"
                        value={p.number}
                        onChange={(e) => updatePhone(p.id, 'number', e.target.value)}
                        placeholder="(555) 000-0000"
                        className="w-full px-3 py-3 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-blue-300 focus:bg-white transition"
                      />
                      <input
                        value={p.label}
                        onChange={(e) => updatePhone(p.id, 'label', e.target.value)}
                        placeholder="Label (Mobile, Work…)"
                        className="w-full px-3 py-2 text-xs border border-gray-100 rounded-xl bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-blue-200 focus:bg-white transition text-gray-500"
                      />
                    </div>
                    {draft.phones.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePhone(p.id)}
                        className="mt-2 w-9 h-9 flex items-center justify-center rounded-full bg-red-50 hover:bg-red-100 transition-colors shrink-0"
                        title="Remove this phone"
                        aria-label={`Remove phone ${idx + 1}`}
                      >
                        <Trash2 size={14} className="text-red-500" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Email rows */}
            <div className="py-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  Email
                </label>
                <button
                  type="button"
                  onClick={addEmail}
                  className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
                >
                  <Plus size={12} /> Add email
                </button>
              </div>
              <div className="space-y-2">
                {draft.emails.map((e, idx) => (
                  <div key={e.id} className="flex gap-2 items-start">
                    <div className="flex-1 space-y-1">
                      <input
                        type="email"
                        inputMode="email"
                        value={e.address}
                        onChange={(ev) => updateEmail(e.id, 'address', ev.target.value)}
                        placeholder="jane@example.com"
                        className="w-full px-3 py-3 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-blue-300 focus:bg-white transition"
                      />
                      <input
                        value={e.label}
                        onChange={(ev) => updateEmail(e.id, 'label', ev.target.value)}
                        placeholder="Label (Personal, Work…)"
                        className="w-full px-3 py-2 text-xs border border-gray-100 rounded-xl bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-blue-200 focus:bg-white transition text-gray-500"
                      />
                    </div>
                    {draft.emails.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEmail(e.id)}
                        className="mt-2 w-9 h-9 flex items-center justify-center rounded-full bg-red-50 hover:bg-red-100 transition-colors shrink-0"
                        title="Remove this email"
                        aria-label={`Remove email ${idx + 1}`}
                      >
                        <Trash2 size={14} className="text-red-500" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Preferred contact method */}
            <div className="py-4">
              <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                Preferred Contact Method
              </label>
              <div className="flex gap-2">
                {(['email', 'phone', 'mail'] as PreferredMethod[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, preferredMethod: m }))}
                    className={`flex-1 py-3 text-sm font-medium rounded-xl border transition-colors ${
                      draft.preferredMethod === m
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'border-gray-200 text-gray-600 bg-white hover:border-blue-300'
                    }`}
                  >
                    {m === 'mail' ? 'Postal Mail' : m.charAt(0).toUpperCase() + m.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Attributes */}
            <div className="py-4 divide-y divide-gray-50">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                Attributes
              </p>
              <label className="flex items-center justify-between py-3 cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-gray-800">Responsible Party</span>
                  <p className="text-[11px] text-gray-400">Primary contact named on the inspection</p>
                </div>
                <Toggle on={!!draft.isResponsibleParty} onToggle={() => toggle('isResponsibleParty')} />
              </label>
              <label className="flex items-center justify-between py-3 cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-gray-800">Billing Contact</span>
                  <p className="text-[11px] text-gray-400">Receives billing invoices</p>
                </div>
                <Toggle on={!!draft.isBillingContact} onToggle={() => toggle('isBillingContact')} />
              </label>
              <label className="flex items-center justify-between py-3 cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-gray-800">Receives Inspection Letter</span>
                  <p className="text-[11px] text-gray-400">Will receive the inspection report</p>
                </div>
                <Toggle on={!!draft.receivesLetter} onToggle={() => toggle('receivesLetter')} />
              </label>
            </div>

          </div>

          {/* Remove contact (edit mode only) */}
          {onDelete && (
            <div className="px-4 pt-1 pb-3">
              <button
                onClick={onDelete}
                className="w-full py-3 text-sm font-semibold text-red-500 bg-red-50 rounded-2xl hover:bg-red-100 transition-colors"
              >
                Remove Contact
              </button>
            </div>
          )}

          {/* Safe-area spacer so the Done button doesn't overlap scrolled content */}
          <div className="h-4" />
        </div>

        {/* ── Sticky Done button — always visible above safe-area ── */}
        <div className="shrink-0 px-4 pt-3 pb-5 border-t border-gray-100 bg-white">
          <button
            onClick={handleDone}
            disabled={!canSubmit}
            className="w-full py-4 rounded-2xl text-[16px] font-semibold text-white transition-opacity disabled:opacity-40"
            style={{ backgroundColor: canSubmit ? '#2563eb' : '#93c5fd' }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// ContactsModulePreview — main exported component
// =============================================================================

// Stable empty-array defaults for optional list props. Inline `= []` defaults
// create a NEW array identity on every render, which makes the
// initialContacts-sync effect below fire on every render (setContacts →
// re-render → new default → effect → ...) — an infinite re-render loop that
// starts as soon as any other state change triggers a second render. Hoisted
// to module constants so the identities are stable across renders.
const EMPTY_CONTACT_ENTRIES: ContactEntry[] = [];
const EMPTY_PANEL_CONTACTS: PanelContact[] = [];

/**
 * @component ContactsModulePreview
 *
 * Purpose:
 * - Renders the Contacts module for template preview (system-web and tenant-web)
 *   and is the reference implementation for the mobile inspector app.
 *
 * Props:
 * - config: ContactsModuleConfig from the template definition.
 * - onChanges: Fired with the diff every time contacts change. Used by Session Data panel.
 * - initialContacts: Starting contact list (empty by default for preview).
 * - contactPool: Available case contacts to pick from (empty by default for preview).
 * - onContactsChange: Optional callback for raw contact list changes.
 *
 * Behavior:
 * - Supports add new contact, pick from pool, edit, and delete operations.
 * - Groups contacts by configured role; unrecognized roles appended at end.
 * - Shows requirement status bar when minRequired > 0 for any role.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - No API calls; caller owns persistence and pool data.
 * - The ContactEntry, ContactChange, and onChanges contract are fixed shared interfaces.
 *
 * Performance:
 * - Local state only; no subscriptions.
 */
export function ContactsModulePreview({
  config,
  onChanges,
  initialContacts = EMPTY_CONTACT_ENTRIES,
  contactPool = EMPTY_CONTACT_ENTRIES,
  onContactsChange,
  mode = 'session',
  panelContactPool = EMPTY_PANEL_CONTACTS,
  entityContacts = EMPTY_PANEL_CONTACTS,
  entityName = '',
  availableRoles,
  onCreateNew,
  onLinkExistingToEntity,
  mapboxToken,
  mapboxProximity,
}: {
  config: ContactsModuleConfig;
  onChanges?: (changes: ContactChange[]) => void;
  initialContacts?: ContactEntry[];
  contactPool?: ContactEntry[];
  onContactsChange?: (contacts: ContactEntry[]) => void;
  /**
   * Selects the add-contact UX. When 'persisted', the typeahead-with-create
   * flow is rendered and the host must wire panelContactPool + onCreateNew.
   * Defaults to 'session' for backward compatibility.
   */
  mode?: ContactsModulePreviewMode;
  /**
   * System contact pool, sourced live from the host's Dexie `contacts` table.
   * Used by the typeahead in 'persisted' mode. Ignored in 'session' mode.
   */
  panelContactPool?: PanelContact[];
  /**
   * Contacts already attached to the entity associated with this inspection.
   * Used to suppress / drive the "Also add to {entityName}?" prompt in the
   * typeahead. Only meaningful in 'persisted' mode.
   */
  entityContacts?: PanelContact[];
  /**
   * Display name of the entity the inspection belongs to. Used in the
   * "Also add to {entityName}?" prompt.
   */
  entityName?: string;
  /**
   * Optional Dexie-backed list of contact role display names. Forwarded to
   * the typeahead's inline create form so role pills come from the host's
   * lookup data instead of the ui-admin localStorage stub.
   */
  availableRoles?: string[];
  /**
   * Local-first create handler. Called when the inspector submits the
   * inline create form. The host should call contactService.createContact
   * (Dexie-first), then return the saved record adapted to PanelContact.
   * The shared component will then attach the new contact to the inspection
   * session list via the existing onChanges / onContactsChange contract.
   */
  onCreateNew?: (input: NewContactInput) => Promise<PanelContact>;
  /**
   * Local-first entity-link handler. Called after the user confirms the
   * "Also add to {entityName}?" prompt. The host should call
   * entityService.addContactToEntity().
   */
  onLinkExistingToEntity?: (contact: ContactEntry, role: string) => Promise<void>;
  /**
   * Optional public Mapbox client token, threaded to the typeahead's inline
   * create/edit form so its StructuredAddressField gets the interactive address
   * picker + USPS validation. Omit to fall back to plain structured inputs.
   */
  mapboxToken?: string;
  /**
   * Optional bias point (e.g. the tenant's department coverage centroid) so
   * as-you-type address suggestions near the department rank above equally-
   * valid distant matches. Never excludes results.
   */
  mapboxProximity?: AddressProximity;
}) {
  // ── ZONE 1: state and handler logic ──

  const roleOptions = config.roles.filter((r) => r.role).map((r) => r.role);
  const firstRole = roleOptions[0] || '';

  const initialContactsRef = useRef<ContactEntry[]>(initialContacts);
  const [contacts, setContacts] = useState<ContactEntry[]>(initialContacts);
  const [sheet, setSheet] = useState<SheetState>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [typeaheadSelected, setTypeaheadSelected] = useState<PanelContact | null>(null);
  const { theme: gridTheme } = useTheme();

  useEffect(() => {
    initialContactsRef.current = initialContacts;
    setContacts(initialContacts);
  }, [initialContacts]);

  /**
   * Compares the current contact list against the seeded baseline and returns
   * ContactChange objects describing what the inspector changed.
   * DO NOT modify — the output format is a shared contract.
   */
  const buildChanges = useCallback((next: ContactEntry[]): ContactChange[] => {
    const now = new Date().toISOString();
    const out: ContactChange[] = [];
    const seed = initialContactsRef.current;
    const seedIds = new Set(seed.map((s) => s.id));
    next
      .filter((c) => !seedIds.has(c.id))
      .forEach((c) =>
        out.push({ kind: 'added', contactId: c.id, name: c.name, roles: c.roles, changedAt: now }),
      );
    next
      .filter((c) => seedIds.has(c.id))
      .forEach((c) => {
        const orig = seed.find((s) => s.id === c.id)!;
        const phonesChanged =
          JSON.stringify(orig.phones ?? []) !== JSON.stringify(c.phones ?? []);
        const emailsChanged =
          JSON.stringify(orig.emails ?? []) !== JSON.stringify(c.emails ?? []);
        const rolesChanged =
          JSON.stringify([...(orig.roles ?? [])].sort()) !== JSON.stringify([...(c.roles ?? [])].sort());
        if (
          orig.name !== c.name ||
          rolesChanged ||
          phonesChanged ||
          emailsChanged ||
          orig.isResponsibleParty !== c.isResponsibleParty ||
          orig.isBillingContact !== c.isBillingContact ||
          orig.receivesLetter !== c.receivesLetter ||
          orig.preferredMethod !== c.preferredMethod
        ) {
          out.push({ kind: 'edited', contactId: c.id, name: c.name, roles: c.roles, changedAt: now });
        }
      });
    seed.filter((s) => !next.find((c) => c.id === s.id)).forEach((s) =>
      out.push({ kind: 'deleted', contactId: s.id, name: s.name, roles: s.roles, changedAt: now }),
    );
    return out;
  }, []);

  const handleAdd = (c: ContactEntry) => {
    const next = [...contacts, { ...c, id: crypto.randomUUID() }];
    setContacts(next);
    onChanges?.(buildChanges(next));
    onContactsChange?.(next);
    setSheet(null);
  };

  const handleAddFromPool = (c: ContactEntry) => {
    if (contacts.some((existing) => existing.id === c.id)) return;
    const next = [...contacts, c];
    setContacts(next);
    onChanges?.(buildChanges(next));
    onContactsChange?.(next);
    setSheet(null);
  };

  const handleEdit = (updated: ContactEntry) => {
    const next = contacts.map((c) => (c.id === updated.id ? updated : c));
    setContacts(next);
    onChanges?.(buildChanges(next));
    onContactsChange?.(next);
    setSheet(null);
  };

  const handleDelete = (id: string) => {
    const next = contacts.filter((c) => c.id !== id);
    setContacts(next);
    onChanges?.(buildChanges(next));
    onContactsChange?.(next);
    setSheet(null);
  };

  // ── 'persisted' mode helpers ──────────────────────────────────────────────
  // These adapters sit between the typeahead's PanelContact data shape and
  // the module's flat ContactEntry shape. They are intentionally inline so
  // the shared ui-admin component doesn't need to import a tenant-web service.

  const panelToEntry = useCallback((c: PanelContact, assignedRoles: string[]): ContactEntry => {
    const phones = (c.phoneNumbers ?? []).map((p) => ({
      id: p._id,
      number: p.number,
      label: typeof p.type === 'string' ? p.type : '',
    }));
    const emails = (c.emails ?? []).map((e) => ({
      id: e._id,
      address: e.email,
      label: typeof e.type === 'string' ? e.type : '',
    }));
    const primaryPhone = c.phoneNumbers?.find((p) => p.isPrimary)?.number ?? c.phoneNumbers?.[0]?.number ?? '';
    const primaryEmail = c.emails?.find((e) => e.isPrimary)?.email ?? c.emails?.[0]?.email ?? '';
    return {
      id: c._id,
      roles: assignedRoles.length ? assignedRoles : (c.defaultRoles?.length ? c.defaultRoles : (firstRole ? [firstRole] : [])),
      name: c.name,
      phones,
      emails,
      phone: primaryPhone,
      email: primaryEmail,
      preferredMethod: emails.length ? 'email' : phones.length ? 'phone' : 'mail',
    };
  }, [firstRole]);

  /** Typeahead "select existing pool contact" → push into module session. */
  const handleTypeaheadSelect = useCallback((c: PanelContact) => {
    setTypeaheadSelected(c);
    if (contacts.some((existing) => existing.id === c._id)) {
      // Already on the inspection; nothing to do.
      return;
    }
    const entry = panelToEntry(c, []);
    const next = [...contacts, entry];
    setContacts(next);
    onChanges?.(buildChanges(next));
    onContactsChange?.(next);
  }, [contacts, panelToEntry, buildChanges, onChanges, onContactsChange]);

  /** Typeahead "create new contact" → host writes to Dexie, returns the
   *  PanelContact so the typeahead can call onSelect → handleTypeaheadSelect,
   *  which handles adding the entry to the inspection session list.
   *  We do NOT add the contact here to avoid duplicates with handleTypeaheadSelect. */
  const handleTypeaheadCreate = useCallback(async (input: NewContactInput): Promise<PanelContact | undefined> => {
    if (!onCreateNew) return undefined;
    try {
      const created = await onCreateNew(input);
      if (!created) return undefined;
      return created;
    } catch (err) {
      console.error('[ContactsModulePreview] onCreateNew failed', err);
      return undefined;
    }
  }, [onCreateNew]);

  /** Typeahead "Also add to {entityName}?" → forward to host. */
  const handleTypeaheadLinkToEntity = useCallback((c: PanelContact, role: string) => {
    if (!onLinkExistingToEntity) return;
    const entry = panelToEntry(c, role ? [role] : []);
    void onLinkExistingToEntity(entry, role);
  }, [onLinkExistingToEntity, panelToEntry]);

  const handleTypeaheadClear = useCallback(() => {
    setTypeaheadSelected(null);
  }, []);

  /** Already-on-the-inspection list (for typeahead dedup badges). */
  const caseContactsForTypeahead = useMemo<PanelContact[]>(() => {
    return contacts.map((c) => ({
      _id: c.id,
      name: c.name,
      phoneNumbers: (c.phones ?? []).map((p, idx) => ({
        _id: p.id ?? `ph-${c.id}-${idx}`,
        type: (p.label || 'Mobile') as any,
        number: p.number,
        isPrimary: idx === 0,
      })),
      emails: (c.emails ?? []).map((e, idx) => ({
        _id: e.id ?? `em-${c.id}-${idx}`,
        type: (e.label || 'Business') as any,
        email: e.address,
        isPrimary: idx === 0,
      })),
      defaultRoles: c.roles ?? [],
      isPrimary: c.isResponsibleParty,
    }));
  }, [contacts]);

  // A contact with roles: ['Owner', 'Responsible Party'] appears under BOTH groups.
  const grouped = roleOptions.length > 0
    ? roleOptions.map((role) => ({ role, items: contacts.filter((c) => c.roles.includes(role)) }))
    : [{ role: '', items: contacts }];
  const extraRoles = [
    ...new Set(
      contacts.flatMap((c) => c.roles.filter((r) => !roleOptions.includes(r))),
    ),
  ];
  const allGroups = [
    ...grouped,
    ...extraRoles.map((role) => ({ role, items: contacts.filter((c) => c.roles.includes(role)) })),
  ];

  const requiredRoles = config.roles.filter((r) => r.role && r.minRequired > 0);
  const allRequirementsMet = requiredRoles.every(
    (r) => contacts.filter((c) => c.roles.includes(r.role)).length >= r.minRequired,
  );

  // ── ZONE 2: UI ──

  return (
    <div className="relative bg-gray-50" style={{ minHeight: 560 }}>

      {/* App bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-blue-600" />
          <span className="font-semibold text-gray-900 text-[15px]">Contacts</span>
          {contacts.length > 0 && (
            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">
              {contacts.length}
            </span>
          )}
        </div>
        {(config.allowSelectExisting || config.allowAddNew) && mode === 'session' && (
          <div className="flex items-center gap-2">
            {config.allowSelectExisting && (
              <button
                onClick={() => setSheet({ mode: 'pool' })}
                className="flex items-center gap-1 px-2.5 py-1.5 text-[12px] font-semibold text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-50 transition-colors"
                title="Add from case contacts"
              >
                <Search size={12} /> Find
              </button>
            )}
            {config.allowAddNew && (
              <button
                onClick={() => setSheet({ mode: 'add' })}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold text-white rounded-xl transition-colors"
                style={{ backgroundColor: '#2563eb' }}
              >
                <Plus size={13} /> New
              </button>
            )}
          </div>
        )}
        {mode === 'persisted' && (config.allowSelectExisting || config.allowAddNew) && (
          <button
            onClick={() => setShowAddPanel((prev) => !prev)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-xl transition-colors ${
              showAddPanel
                ? 'bg-blue-600 text-white border border-blue-600'
                : 'text-blue-600 border border-blue-200 hover:bg-blue-50'
            }`}
            title="Search system contacts"
          >
            {showAddPanel ? 'Cancel' : (<><Plus size={12} /> Add Contact</>)}
          </button>
        )}
      </div>

      {/* Persisted-mode typeahead — single block of code that mirrors the
          tenant-web ContactsPanel / CaseTimeline add-contact UX. The host
          adapter wires panelContactPool, entityContacts, onCreateNew, and
          onLinkExistingToEntity to its Dexie-backed services. */}
      {mode === 'persisted' && showAddPanel && (
        <div className="px-4 py-3 border-b border-gray-100 bg-white">
          <ContactSearchTypeahead
            contactPool={panelContactPool}
            caseContacts={caseContactsForTypeahead}
            entityContacts={entityContacts}
            entityName={entityName || 'this entity'}
            theme={gridTheme}
            selectedContact={typeaheadSelected}
            onSelect={handleTypeaheadSelect}
            onClear={handleTypeaheadClear}
            onCreateContact={config.allowAddNew ? handleTypeaheadCreate : undefined}
            onLinkToEntity={onLinkExistingToEntity ? handleTypeaheadLinkToEntity : undefined}
            availableRoles={availableRoles}
            mapboxToken={mapboxToken}
            mapboxProximity={mapboxProximity}
          />
        </div>
      )}

      {/* Requirements status bar */}
      {requiredRoles.length > 0 && (
        <div className={`px-4 py-2.5 flex items-center gap-2 border-b text-xs ${allRequirementsMet ? 'bg-green-50 border-green-100' : 'bg-amber-50 border-amber-100'}`}>
          <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${allRequirementsMet ? 'bg-green-500' : 'bg-amber-500'}`} />
          <span className={`font-medium ${allRequirementsMet ? 'text-green-700' : 'text-amber-700'}`}>
            {allRequirementsMet
              ? 'All required contacts added'
              : requiredRoles
                  .filter((r) => contacts.filter((c) => c.roles.includes(r.role)).length < r.minRequired)
                  .map((r) => {
                    const have = contacts.filter((c) => c.roles.includes(r.role)).length;
                    return `${r.role} (${have}/${r.minRequired})`;
                  })
                  .join(' · ') + ' still needed'}
          </span>
        </div>
      )}

      {/* Contact list */}
      <div>
        {contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-6">
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-4">
              <Users size={28} className="text-blue-300" />
            </div>
            <div className="text-[15px] font-semibold text-gray-700 mb-1">No contacts yet</div>
            <div className="text-sm text-gray-400 leading-relaxed">
              {config.allowAddNew
                ? 'Tap Add to record the property owner, responsible party, or other contacts for this inspection.'
                : 'Contacts will appear here when assigned.'}
            </div>
          </div>
        ) : (
          <div className="py-2">
            {allGroups
              .filter(
                (g) =>
                  g.items.length > 0 ||
                  (config.roles.find((r) => r.role === g.role)?.minRequired ?? 0) > 0,
              )
              .map((group) => {
                const req = config.roles.find((r) => r.role === group.role);
                const minReq = req?.minRequired ?? 0;
                const met = group.items.length >= minReq;
                return (
                  <div key={group.role || '_all'} className="mb-1">
                    {group.role && (
                      <div className="px-4 py-1.5 flex items-center gap-2">
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                          {group.role}
                        </span>
                        {minReq > 0 && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${met ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                            {group.items.length}/{minReq} req.
                          </span>
                        )}
                      </div>
                    )}
                    <div className="bg-white mx-3 rounded-2xl overflow-hidden border border-gray-100 shadow-xs">
                      {group.items.map((c, idx) => (
                        <div key={c.id}>
                          {idx > 0 && <div className="h-px bg-gray-100 ml-16" />}
                          <div className="flex items-start gap-3 px-4 py-3">
                            <AvatarCircle name={c.name} />
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-gray-900 text-[14px] truncate">{c.name}</div>
                              {!group.role && c.roles.length > 0 && (
                                <div className="text-xs text-blue-600 font-medium">{c.roles.join(', ')}</div>
                              )}
                              {/* Multi-row phones */}
                              {(c.phones ?? []).filter((p) => p.number).map((p) => (
                                <div key={p.id} className="text-xs text-gray-500 truncate mt-0.5 flex items-center gap-1">
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-green-600"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.62 3.38 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.8a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                                  <span>{p.number}{p.label ? ` · ${p.label}` : ''}</span>
                                </div>
                              ))}
                              {/* Multi-row emails */}
                              {(c.emails ?? []).filter((e) => e.address).map((e) => (
                                <div key={e.id} className="text-xs text-gray-500 truncate mt-0.5 flex items-center gap-1">
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-blue-500"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                                  <span>{e.address}{e.label ? ` · ${e.label}` : ''}</span>
                                </div>
                              ))}
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {c.isResponsibleParty && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">Responsible Party</span>
                                )}
                                {c.isBillingContact && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">Billing</span>
                                )}
                                {c.receivesLetter && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold">Gets Letter</span>
                                )}
                                {c.preferredMethod === 'mail' && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">Postal Mail</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0 pt-0.5">
                              {(c.phones ?? []).some((p) => p.number) && (
                                <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center" title="Has phone">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.62 3.38 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.8a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                                </div>
                              )}
                              {(c.emails ?? []).some((e) => e.address) && (
                                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center" title="Has email">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                                </div>
                              )}
                              {config.allowAddNew && (
                                <button
                                  onClick={() => setSheet({ mode: 'edit', contact: c })}
                                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                                  title="Edit"
                                >
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                </button>
                              )}
                              {confirmDeleteId === c.id ? (
                                <>
                                  <button
                                    onClick={() => { handleDelete(c.id); setConfirmDeleteId(null); }}
                                    className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center hover:bg-red-200 transition-colors"
                                    title="Confirm remove"
                                  >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                  </button>
                                  <button
                                    onClick={() => setConfirmDeleteId(null)}
                                    className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                                    title="Cancel"
                                  >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => setConfirmDeleteId(c.id)}
                                  className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center hover:bg-red-100 transition-colors"
                                  title="Remove contact"
                                >
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {group.items.length === 0 && (
                        <div className="flex items-center gap-3 px-4 py-3 text-sm text-gray-400 italic">
                          <div className="w-10 h-10 rounded-full border-2 border-dashed border-gray-200 flex items-center justify-center shrink-0">
                            <Plus size={14} className="text-gray-300" />
                          </div>
                          No {group.role.toLowerCase()} added
                          {minReq > 0 && <span className="text-red-400 font-medium not-italic">— required</span>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Sheets */}
      {sheet?.mode === 'pool' && (
        <ContactPoolPicker
          pool={contactPool}
          alreadyAdded={contacts.map((c) => c.id)}
          onSelect={handleAddFromPool}
          onCancel={() => setSheet(null)}
        />
      )}
      {sheet?.mode === 'add' && (
        <ContactSheet
          initial={BLANK_CONTACT(firstRole)}
          roleOptions={roleOptions}
          submitLabel="Add Contact"
          onSubmit={handleAdd}
          onCancel={() => setSheet(null)}
        />
      )}
      {sheet?.mode === 'edit' && (
        <ContactSheet
          initial={sheet.contact}
          roleOptions={roleOptions}
          submitLabel="Edit Contact"
          onSubmit={handleEdit}
          onCancel={() => setSheet(null)}
          onDelete={() => handleDelete(sheet.contact.id)}
        />
      )}
    </div>
  );
}
